import Auth from 'auth';
import { environmentOptions } from 'utils/helpers';

/**
 * The AI provider proxy on api.epublishing.com.
 *
 * Continuum used to call api.anthropic.com and api.openai.com straight from
 * the browser, which meant shipping a provider credential to the browser to do
 * it — an Anthropic key obfuscated into the bundle, and each tenant's OpenAI
 * key read from the `openai.api.key` system setting. Both were readable by
 * anyone with devtools.
 *
 * The proxy (api: `app/controllers/ai/proxy_controller.rb`) holds both
 * credentials server-side and forwards request and response bodies verbatim,
 * so call sites keep speaking each provider's own protocol — the only thing
 * that changes here is the URL and the headers.
 *
 * The reachable endpoints are exactly the routes the api declares; a path not
 * in this map is not proxied.
 */
export const AI_PROXY_PATHS = {
  anthropicMessages: '/ai/anthropic/v1/messages',
  openaiChat: '/ai/openai/v1/chat/completions',
  openaiCompletions: '/ai/openai/v1/completions',
  openaiImages: '/ai/openai/v1/images/generations',
} as const;

/**
 * Absolute URL for a proxy endpoint, on whichever api the rest of the admin is
 * already talking to — production, or stage when `api.staging.enabled` is set
 * in localStorage. Derived from the GraphQL endpoint so the two can never
 * disagree about which environment is being addressed.
 *
 * @param path One of AI_PROXY_PATHS.
 */
export function aiProxyUrl(path: string): string {
  const { URI } = environmentOptions();
  return new URL(path, URI).toString();
}

/**
 * Headers for a proxy request.
 *
 * Authorization is the same admin token, per-tenant signing secret and
 * active-user check the GraphQL endpoint uses — the proxy requires an admin
 * token and 403s without one. No provider credential is sent: the server
 * attaches that.
 */
export function aiProxyHeaders(): Record<string, string> {
  const { X_ENV, X_HOST_SHORTNAME } = environmentOptions();

  return {
    Authorization: `token ${Auth.getToken()}`,
    'X-Environment': X_ENV,
    'X-Host-Shortname': X_HOST_SHORTNAME,
    'Content-Type': 'application/json',
  };
}

/**
 * The error message in a failed proxy response body, whichever side failed.
 *
 * When the provider rejects a request the proxy forwards its body verbatim,
 * and both OpenAI and Anthropic put the reason at `error.message`. When the
 * proxy refuses the request itself (no credential configured for the site, a
 * provider timeout, the rate limit) it answers `{ errors: [message] }`
 * instead. Reading only the provider shape hides those, and they are the ones
 * a retry won't fix.
 *
 * @param body The parsed response body, or whatever a failed request left.
 */
export function aiProxyErrorMessage(body: any): string | undefined {
  const providerMessage = body?.error?.message;
  if (typeof providerMessage === 'string' && providerMessage) return providerMessage;

  const proxyMessages = Array.isArray(body?.errors) ? body.errors.filter(Boolean) : [];
  return proxyMessages.length ? proxyMessages.join(' ') : undefined;
}
